import { Link } from "../components/Link";
import type { AuditLogAction } from "@avisoma/shared";

export default function AuditLogDataDetails({
  data,
}: {
  data: AuditLogAction;
}) {
  if (data.action === "upload-document") {
    return wrap(
      <>
        Uploaded
        <span>
          <Link to={`/documents/${data.data.documentId}`}>document</Link>.
        </span>
      </>
    );
  }

  if (data.action === "set-document-category") {
    return wrap(
      <>
        Set
        <Link to={`/documents/${data.data.documentId}`}>
          document
          {/* {id(data.data.documentId)} */}
        </Link>
        category from
        {renderCategory(data.data.previousCategory)}
        to
        <span>{renderCategory(data.data.newCategory)}.</span>
      </>
    );
  }

  if (data.action === "set-document-page-category") {
    return wrap(
      <>
        Set
        <Link to={`/document-pages/${data.data.pageId}`}>
          page
          {/* {id(data.data.pageId)} */}
        </Link>
        category from
        {renderCategory(data.data.previousCategory)}
        to
        <span>{renderCategory(data.data.newCategory)}.</span>
      </>
    );
  }

  if (data.action === "confirm-page-category-classification") {
    return wrap(
      <>
        Confirmed
        {renderCategory(data.data.classificationCategory)}
        category match for
        <span>
          <Link to={`/document-pages/${data.data.pageId}`}>page</Link>.
        </span>
      </>
    );
  }

  if (data.action === "reject-page-category-classification") {
    return wrap(
      <>
        Rejected
        {renderCategory(data.data.classificationCategory)}
        category match for
        <Link to={`/document-pages/${data.data.pageId}`}>page.</Link>
        Fixed to
        <span>{renderCategory(data.data.fixedCategory)}.</span>
      </>
    );
  }

  if (data.action === "category.rename") {
    // Only the non-zero counts are listed. A rename usually touches one or two of the
    // seven columns, so spelling out every zero would bury the part that actually moved.
    const rewritten = renameRewriteCounts(data.data);
    return wrap(
      <>
        Renamed category
        {renderCategory(data.data.oldName)}
        to
        <span>{renderCategory(data.data.newName)}.</span>
        <span>
          {rewritten.length === 0
            ? "No stored references needed updating."
            : `Updated ${rewritten.join(", ")}.`}
        </span>
      </>
    );
  }

  if (data.action === "category.update-description") {
    return wrap(
      <>
        Updated description of
        {renderCategory(data.data.name)}
        from
        {renderDescription(data.data.oldDescription)}
        to
        <span>{renderDescription(data.data.newDescription)}.</span>
      </>
    );
  }

  if (data.action === "category.delete") {
    // The category row is gone, so there is nothing to link to — the recorded name is
    // the only handle left on it.
    return wrap(
      <>
        Deleted category
        {renderCategory(data.data.name)}
        <span>
          along with {data.data.deletedClusters} cluster(s), {data.data.deletedRcEmbeddings} RC embedding(s),{" "}
          {data.data.deletedClassifications} classification(s) and {data.data.deletedOutlierCandidates} outlier
          candidate(s).
        </span>
      </>
    );
  }

  // @ts-expect-error — data.data union is too narrow upstream; falls through here.
  const otherData = data.data as Record<string, unknown> | string;
  return typeof otherData === "object"
    ? Object.entries(otherData)
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n")
    : String(otherData);
}

const wrap = (children: React.ReactNode) => (
  <div className="flex flex-wrap items-center gap-1">{children}</div>
);

/**
 * The rename's per-column rewrite counts, as readable phrases, with the zeros dropped.
 * Column names would mean nothing to a reader of the audit trail, so each one is
 * described by what it is rather than where it lives.
 */
const renameRewriteCounts = (data: {
  updatedDocuments: number;
  updatedDocumentUserSuggestedCategories: number;
  updatedPageUserSuggestedCategories: number;
  updatedReconstructedDocumentClassifications: number;
  updatedReconstructedDocumentUserSuggestedCategories: number;
  updatedOutlierOwnCategoryNames: number;
  updatedOutlierRivalCategoryNames: number;
}) =>
  (
    [
      [data.updatedDocuments, "document", "documents"],
      [data.updatedDocumentUserSuggestedCategories, "document suggestion", "document suggestions"],
      [data.updatedPageUserSuggestedCategories, "page suggestion", "page suggestions"],
      [
        data.updatedReconstructedDocumentClassifications,
        "reconstructed document",
        "reconstructed documents",
      ],
      [
        data.updatedReconstructedDocumentUserSuggestedCategories,
        "reconstructed suggestion",
        "reconstructed suggestions",
      ],
      [data.updatedOutlierOwnCategoryNames, "outlier candidate", "outlier candidates"],
      [data.updatedOutlierRivalCategoryNames, "outlier rival", "outlier rivals"],
    ] as const
  )
    .filter(([count]) => count > 0)
    .map(([count, one, many]) => `${count} ${count === 1 ? one : many}`);

const renderDescription = (description: string | null) => {
  if (!description) {
    return (
      <span className="text-gray-600 dark:text-zinc-400 font-thin">null</span>
    );
  }

  return <span className="font-bold">{description}</span>;
};

const renderCategory = (category: string | null) => {
  if (!category) {
    return (
      <span className="text-gray-600 dark:text-zinc-400 font-thin">null</span>
    );
  }

  return <span className="font-bold">{category}</span>;
};
